import { create } from "zustand";
import type {
  CollectionWithPois,
  CollectionVisibility,
} from "@poi-planner/shared";
import { api } from "../services/api";

interface CollectionsState {
  // Data
  collections: CollectionWithPois[];
  loading: boolean;
  error: string | null;

  // Actions
  loadCollections: () => Promise<void>;
  createCollection: (
    name: string,
    description?: string,
    visibility?: CollectionVisibility,
  ) => Promise<string>;
  updateCollection: (
    id: string,
    updates: {
      name?: string;
      description?: string;
      visibility?: CollectionVisibility;
    },
  ) => Promise<void>;
  deleteCollection: (id: string) => Promise<void>;
  addPoisToCollection: (
    collectionId: string,
    poiIds: string[],
  ) => Promise<void>;
  removePoisFromCollection: (
    collectionId: string,
    poiIds: string[],
  ) => Promise<void>;

  // Helpers
  getCollection: (id: string) => CollectionWithPois | undefined;
  getCollectionsForPoi: (poiId: string) => CollectionWithPois[];
}

export const useCollectionsStore = create<CollectionsState>((set, get) => ({
  collections: [],
  loading: false,
  error: null,

  loadCollections: async () => {
    set({ loading: true, error: null });
    try {
      const collections = await api.listCollections();
      set({ collections, loading: false });
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  createCollection: async (name, description, visibility = "private") => {
    const collection = await api.createCollection(
      name,
      description,
      visibility,
    );
    // Reload to get full collection with metadata
    await get().loadCollections();
    return collection.id;
  },

  updateCollection: async (id, updates) => {
    await api.updateCollection(id, updates);
    // Update local state
    const { collections } = get();
    const idx = collections.findIndex((c) => c.id === id);
    if (idx !== -1) {
      const updated = {
        ...collections[idx],
        ...updates,
        updatedAt: new Date().toISOString(),
      };
      const newCollections = [...collections];
      newCollections[idx] = updated;
      set({ collections: newCollections });
    }
  },

  deleteCollection: async (id) => {
    await api.deleteCollection(id);
    const { collections } = get();
    set({ collections: collections.filter((c) => c.id !== id) });
  },

  addPoisToCollection: async (collectionId, poiIds) => {
    await api.addPoisToCollection(collectionId, poiIds);
    // Update local state
    const { collections } = get();
    const idx = collections.findIndex((c) => c.id === collectionId);
    if (idx !== -1) {
      const existing = new Set(collections[idx].poiIds);
      poiIds.forEach((id) => existing.add(id));
      const updated = { ...collections[idx], poiIds: Array.from(existing) };
      const newCollections = [...collections];
      newCollections[idx] = updated;
      set({ collections: newCollections });
    }
  },

  removePoisFromCollection: async (collectionId, poiIds) => {
    await api.removePoisFromCollection(collectionId, poiIds);
    // Update local state
    const { collections } = get();
    const idx = collections.findIndex((c) => c.id === collectionId);
    if (idx !== -1) {
      const toRemove = new Set(poiIds);
      const updated = {
        ...collections[idx],
        poiIds: collections[idx].poiIds.filter((id) => !toRemove.has(id)),
      };
      const newCollections = [...collections];
      newCollections[idx] = updated;
      set({ collections: newCollections });
    }
  },

  getCollection: (id) => {
    return get().collections.find((c) => c.id === id);
  },

  getCollectionsForPoi: (poiId) => {
    return get().collections.filter((c) => c.poiIds.includes(poiId));
  },
}));
